import { MembershipTier } from './gql/generated/typesClient/global';
import { ParsedUserJwt } from './brooklyn-jwt/parseUserJwt';
import { MembershipTierWithAnon } from './types/MembershipTierWithAnon';

const { Premium, Digital, Free } = MembershipTier;

interface Args {
  parsedJwt: null | ParsedUserJwt;
  currentUser: null | {
    membership: boolean | null;
    membership_is_valid: boolean;
    membership_tier: MembershipTier;
    subscription_gateway_shortname: string | null;
  };

  entitlementsEnabled: boolean;
  log?: (msg: string) => void;
}

// Ordered by precedence
const contentSetIdsAndTier = [
  { contentSetId: 300, tier: Premium },
  // `281` was mistakenly applied to annual plans, because these entitlements were written and are
  // now out in the wild, we need to support this (was corrected to `254` on March 30 2023)
  { contentSetId: 281, tier: Digital },
  { contentSetId: 254, tier: Digital },
  // deprecated 291: 'live',
];

export const getAccessTierLegacy = ({ currentUser }: Pick<Args, 'currentUser'>) => {
  if (currentUser?.membership && currentUser?.membership_is_valid) {
    return currentUser.membership_tier;
  }
  return Free;
};

export const getTierFromUserJwt = ({ parsedJwt }: { parsedJwt: null | ParsedUserJwt }) => {
  if (Array.isArray(parsedJwt?.subscriptions)) {
    const match = contentSetIdsAndTier.find(o => parsedJwt?.subscriptions.includes(o.contentSetId));
    if (match) return match.tier;
  }
  return Free;
};

const getFlagMsg = (flag: boolean) => `(entitlements flag: ${flag ? 'on' : 'off'})`;

export const getAccessTier = ({
  parsedJwt,
  currentUser,
  log = () => undefined,
}: Omit<Args, 'entitlementsEnabled'>): MembershipTierWithAnon => {
  if (!currentUser) return 'Anon';

  /*
   * Check both access sources, and use the one granting the highest tier
   */
  const accessSources = [
    { name: 'Brooklyn', tier: getTierFromUserJwt({ parsedJwt }) },
    { name: 'Subscriptions table', tier: getAccessTierLegacy({ currentUser }) },
  ];
  const premiumAccess = accessSources.find(as => as.tier === Premium);
  const digitalAccess = accessSources.find(as => as.tier === Digital);
  const access = premiumAccess || digitalAccess;
  if (access) {
    log(`${access.name} granting ${access.tier} access`);
    return access.tier;
  }

  return Free;
};

/*
 * Older implementation of `getAccessTier` which needs to be maintained to support entitlements
 * validation. After validation is complete, we will adapt `getAccessTier` to be more like the
 * logic here, where users that should have JWT-based access will not receive legacy access as
 * a fallback (effectively ending access for many users who have been wrongly receiving it via
 * legacy third parties whose webhooks stopped being received)
 */
export const getAccessTierForEntitlementsValidation = ({
  parsedJwt,
  currentUser,
  entitlementsEnabled,
  log = () => undefined,
}: Args): MembershipTierWithAnon => {
  if (!currentUser) return 'Anon';

  if (!entitlementsEnabled) {
    const tier = getAccessTierLegacy({ currentUser });
    log(`Legacy subscription granting ${tier} access ${getFlagMsg(entitlementsEnabled)}`);
  }

  /*
   * If user's subscription provides a valid entitlement via the Brooklyn user JWT, use it
   */
  if (Array.isArray(parsedJwt?.subscriptions)) {
    const match = contentSetIdsAndTier.find(o => parsedJwt?.subscriptions.includes(o.contentSetId));
    if (match) {
      log(`Brooklyn granting ${match.tier} access ${getFlagMsg(entitlementsEnabled)}`);
      return match.tier;
    }
  }

  /*
   * If the user's subscription is a legacy/non-Gaia gateway, handle it with legacy access logic.
   *
   * `Gaia` gateway subscriptions should be granting access via the JWT. Because `subscriptions`
   * table data could go stale for them, we cannot trust an active status there.
   */
  if (currentUser && currentUser.subscription_gateway_shortname !== 'Gaia') {
    const tier = getAccessTierLegacy({ currentUser });
    log(`Legacy subscription granting ${tier} access ${getFlagMsg(entitlementsEnabled)}`);
  }

  return Free;
};

export const canViewClasses = (tier: MembershipTier | null | 'Anon') =>
  tier === Premium || tier === Digital;

export const canViewSeries = (tier: MembershipTier | null | 'Anon') =>
  tier === Premium || tier === Digital;
